import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// hover/focus 시 teal 테두리 강조. error/disabled 상태는 그대로 두기 위해 :not 으로 제외.
// MUI 기본 primary 색이 우선순위로 덮어쓰는 케이스가 있어 !important 로 보장.
const inputBorderStyle = {
  '& .MuiOutlinedInput-root:hover:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC !important',
  },
  '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC !important',
  },
};

const PasswordToggleAdornment = ({ visible, onToggle }) => (
  <InputAdornment position="end">
    <IconButton tabIndex={-1} aria-label="toggle password visibility" onClick={onToggle} edge="end">
      {visible ? <VisibilityOff sx={{ width: 15, height: 15 }} /> : <Visibility sx={{ width: 15, height: 15 }} />}
    </IconButton>
  </InputAdornment>
);

export const FormInputText = ({
  name = '',
  control,
  disabled,
  label,
  onBlur,
  onChange,
  onKeyDown,
  staticValue,
  rules,
  placeholder,
  type = 'text',
  rows,
  size,
  maxRows,
  minRows,
  inputProps,
  multiline,
  sx,
  className,
  InputProps,
  id,
  tabIndex,
  autoComplete,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const togglePassword = () => setShowPassword(prev => !prev);

  const isPasswordType = type === 'password';
  const inputType = isPasswordType && showPassword ? 'text' : type;

  // 브라우저 비밀번호 관리자는 힌트 없는 password 입력을 로그인 폼으로 보고 저장된 자격증명(로그인한 CRM 계정)을
  // 자동 채움하며, 같은 폼의 아이디 입력까지 함께 채운다. password 는 off 가 무시되므로 new-password 로 막는다.
  // 관리 화면 입력은 남의 정보를 적는 칸이라 자동완성이 필요 없으므로 그 외 타입은 off 가 기본.
  const resolvedAutoComplete = autoComplete ?? (isPasswordType ? 'new-password' : 'off');

  // 호출부가 InputProps를 직접 넘기면 그대로 사용. 그 외 비밀번호 타입이면 토글 버튼 자동 부착.
  const resolvedInputProps =
    InputProps ??
    (isPasswordType
      ? { endAdornment: <PasswordToggleAdornment visible={showPassword} onToggle={togglePassword} /> }
      : undefined);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange: formOnChange, onBlur: formOnBlur, value }, fieldState: { error } }) => {
        // react-hook-form이 관리하는 value(빈 문자열 포함)가 우선, 없으면 staticValue, 둘 다 없으면 빈 문자열
        const displayValue = value ?? staticValue ?? '';

        const handleChange = e => {
          formOnChange(e);
          if (onChange) onChange(e);
        };

        const handleBlur = e => {
          formOnBlur();
          if (onBlur) onBlur(e);
        };

        return (
          <TextField
            id={id}
            name={name}
            value={displayValue}
            type={inputType}
            disabled={disabled}
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error ? error.message : null}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            rows={rows}
            maxRows={maxRows}
            minRows={minRows}
            multiline={multiline}
            size={size}
            fullWidth
            variant="outlined"
            className={className}
            inputProps={{ ...inputProps, tabIndex: tabIndex ?? 0 }}
            InputProps={resolvedInputProps}
            autoComplete={resolvedAutoComplete}
            sx={{ ...inputBorderStyle, ...sx }}
          />
        );
      }}
    />
  );
};
